/**
 * App.jsx — shell, auth gate, navigation.
 *
 * Navigation is a stack driven by Telegram's native back button. No router
 * library: the app is six screens two levels deep, and react-router would be
 * dead weight plus a second back affordance competing with Telegram's own.
 */

import React, { useCallback, useEffect, useState } from "react";
import * as tg from "./lib/telegram";
import { authenticate, Unbound } from "./lib/api";
import Bots from "./screens/Bots";
import BotDetail from "./screens/BotDetail";
import Create from "./screens/Create";
import TraderPicker from "./screens/TraderPicker";
import Deposit from "./screens/Deposit";
import Account from "./screens/Account";
import { C, Button } from "./theme";

export default function App() {
  const [state, setState] = useState("boot");   // boot|unbound|ready|error
  const [connectUrl, setConnectUrl] = useState(null);
  const [error, setError] = useState(null);
  const [stack, setStack] = useState([{ screen: "bots" }]);
  // Held outside the stack so it survives navigating into the picker and back.
  const [draftTrader, setDraftTrader] = useState(null);

  const top = stack[stack.length - 1];

  const push = useCallback((entry) => {
    tg.haptic.tap();
    setStack((s) => [...s, entry]);
  }, []);

  const pop = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const reset = useCallback(() => setStack([{ screen: "bots" }]), []);

  useEffect(() => {
    tg.ready();
    tg.applyTheme();
  }, []);

  // Telegram's back button, shown only when there is somewhere to go back to.
  useEffect(() => tg.setBack(stack.length > 1 ? pop : null), [stack.length, pop]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await authenticate();
        if (!cancelled) setState("ready");
      } catch (e) {
        if (cancelled) return;
        if (e instanceof Unbound) {
          setConnectUrl(e.connectUrl);
          setState("unbound");
        } else {
          setError(e.message);
          setState("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === "boot")    return <Splash />;
  if (state === "unbound") return <Connect url={connectUrl} />;
  if (state === "error")   return <Failed message={error} />;

  return (
    <div style={{ minHeight: "100vh", background: C.bg0, color: C.ink,
                  fontFamily: C.mono, paddingBottom: 28 }}>
      {top.screen === "bots" && (
        <Bots
          onOpen={(id) => push({ screen: "bot", id })}
          onCreate={() => push({ screen: "create" })}
          onAccount={() => push({ screen: "account" })}
          onDeposit={() => push({ screen: "deposit" })}
        />
      )}
      {top.screen === "bot" && <BotDetail botId={top.id} onBack={pop} />}
      {top.screen === "create" && (
        <Create
          trader={draftTrader}
          onPickTrader={() => push({ screen: "picker" })}
          onDone={() => { setDraftTrader(null); reset(); }}
        />
      )}
      {top.screen === "picker" && (
        <TraderPicker onPick={(t) => { setDraftTrader(t); pop(); }} />
      )}
      {top.screen === "deposit" && <Deposit />}
      {top.screen === "account" && (
        <Account onUnlinked={() => setState("unbound")} />
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg0, color: C.ink,
                  fontFamily: C.mono, display: "flex", alignItems: "center",
                  justifyContent: "center", padding: 24, textAlign: "center" }}>
      <div style={{ maxWidth: 320, width: "100%" }}>{children}</div>
    </div>
  );
}

const Splash = () => (
  <Shell>
    <div style={{ fontFamily: C.disp, fontSize: 26, letterSpacing: "0.08em",
                  color: C.ink }}>
      POLY<span style={{ color: C.amber }}>EDGE</span>
    </div>
    <div style={{ fontSize: 11, color: C.dim, marginTop: 10,
                  letterSpacing: "0.12em" }}>
      CONNECTING…
    </div>
  </Shell>
);

/**
 * Unbound is the ordinary first-run state, not a failure — so it reads as an
 * instruction, and the limits are stated here rather than discovered later.
 */
const Connect = ({ url }) => (
  <Shell>
    <div style={{ fontFamily: C.disp, fontSize: 26, letterSpacing: "0.08em",
                  marginBottom: 14 }}>
      POLY<span style={{ color: C.amber }}>EDGE</span>
    </div>
    <p style={{ fontSize: 13, lineHeight: 1.75, color: C.sub, margin: "0 0 22px" }}>
      This Telegram account isn't linked to PolyEdge yet.
      <br /><br />
      Open <b style={{ color: C.ink }}>Settings → Connected Apps</b> on the web
      and tap the link it gives you.
    </p>
    <Button onClick={() => tg.openLink(
      url || "https://polyedge.online/settings?connect=telegram")}>
      Open settings
    </Button>
    <p style={{ fontSize: 11, color: C.dim, lineHeight: 1.7, marginTop: 20 }}>
      Telegram shows bots, P&amp;L and deposits. Withdrawals, key export and
      plan changes stay on the web.
    </p>
  </Shell>
);

const Failed = ({ message }) => (
  <Shell>
    <div style={{ fontSize: 13, color: C.red, marginBottom: 16 }}>
      {message || "Something went wrong."}
    </div>
    <Button variant="ghost" onClick={() => window.location.reload()}>
      Retry
    </Button>
  </Shell>
);
